import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
/* The following are untyped JS (allowJs) — reused from the single-product measurement flow. */
import UploadDocumentsRaw from '../../../../../../Components/UploadDocuments/UploadDocuments';
import { toasterMsg } from '../../../../../../Components/Toaster/Toaster';
import { BombiixModal } from '../../../../../../Components/BombiixModal';
import { fetchDocumentType } from '../../../../../SystemMasters/Store/SystemMasters.action';
import { uploadDocument } from './measurements.service';

interface DocumentTypeOption {
  _id: string;
  name?: string;
}

interface SelectedDocument {
  fileName: string;
  document: File;
  documentType?: DocumentTypeOption;
}

interface UploadDocumentsHandle {
  selectedDocument: () => Promise<SelectedDocument | undefined> | SelectedDocument | undefined;
}

interface UploadDocumentsProps {
  module: string;
  ref: React.Ref<UploadDocumentsHandle>;
  hideName?: boolean;
  documentTypeList?: DocumentTypeOption[];
  accept?: string;
  showGradingGroupError?: boolean;
  setShowGradingGroupError?: (value: boolean) => void;
  onSelectedGradingGroupChange?: () => void;
}
const UploadDocuments = UploadDocumentsRaw as unknown as React.ForwardRefExoticComponent<UploadDocumentsProps>;

interface DocTypeRootState {
  SystemMastersDetails?: { documentTypeList?: DocumentTypeOption[] };
}

interface DocumentUploadModalProps {
  devOrderId: string;
  gradingGroupId: string;
  onClose: () => void;
  onUploaded: () => void | Promise<void>;
}

/**
 * Upload a supporting document for one product under the common grading group. Reuses the shared
 * `UploadDocuments` picker and posts to the bulk upload endpoint, mirroring the single-product
 * `Documents/Document.js` flow (BM-9210 req 7-8).
 */
const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({ devOrderId, gradingGroupId, onClose, onUploaded }) => {
  const { t } = useTranslation('bulk-edit');
  const dispatch = useDispatch();
  const docRef = useRef<UploadDocumentsHandle>(null);
  const [loading, setLoading] = useState(false);
  const [showGradingGroupError, setShowGradingGroupError] = useState(false);
  const documentTypeList = useSelector((root: DocTypeRootState) => root.SystemMastersDetails?.documentTypeList ?? []);

  useEffect(() => {
    dispatch(fetchDocumentType({}, t));
  }, [dispatch, t]);

  const handleUpload = async (): Promise<void> => {
    if (!docRef.current) return;
    const uploadedDoc = await docRef.current.selectedDocument();
    if (!uploadedDoc) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('fileName', uploadedDoc.fileName);
      formData.append('devOrderId', devOrderId);
      formData.append('gradingGroupId', gradingGroupId);
      formData.append('document', uploadedDoc.document);
      formData.append('documentTitle', uploadedDoc.fileName);
      formData.append('documentType', uploadedDoc.documentType?._id ?? '');
      await uploadDocument(formData);
      toasterMsg(t('MEASUREMENTS_MODULE.DOCUMENT_UPLOADED'), 'success');
      await onUploaded();
      onClose();
    } catch (error) {
      toasterMsg(error instanceof Error ? error.message : t('MEASUREMENTS_MODULE.UPLOAD_FAILED'), 'danger');
    } finally {
      setLoading(false);
    }
  };

  return (
    <BombiixModal
      show
      title={t('MEASUREMENTS_MODULE.UPLOAD_DOCUMENT')}
      onClose={onClose}
      onSave={handleUpload}
      saveLabel={t('MEASUREMENTS_MODULE.UPLOAD')}
      saveLoading={loading}
      size="lg"
    >
      <UploadDocuments
        ref={docRef}
        module="specificationDoc"
        hideName
        documentTypeList={documentTypeList}
        accept=".xls, .xlsx, .pdf, .dxf, .rul, .wds"
        showGradingGroupError={showGradingGroupError}
        setShowGradingGroupError={setShowGradingGroupError}
        onSelectedGradingGroupChange={() => {}}
      />
    </BombiixModal>
  );
};

export default DocumentUploadModal;
